import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { FormattedMessage } from 'react-intl';
import cx from 'classnames';
import Slider from 'rc-slider';

import CurrencyConverter from '../../CurrencyConverter';
import messages from '../../../locale/messages';
import {
  FEATURED_PRICE_SLIDER_MAX,
  LOWEST_PRICE_MAX_USD,
  LOWEST_PRICE_MIN_USD,
  LUXURY_PRICE_MAX_USD,
  LUXURY_PRICE_MIN_USD,
} from './featuredCarsData';
import FeaturedCarFilterIcon from './featuredCarFilterIcons';

import s from './FeaturedCars.css';
import cs from '../../commonStyle.css';
import sliderStyles from '!isomorphic-style-loader!css-loader!rc-slider/assets/index.css';

const MOBILE_POPOVER_QUERY = '(max-width: 768px)';
const POPOVER_VIEWPORT_INSET = 20;

// clear inline styles when desktop CSS should take over
const resetPopoverPosition = (popover) => {
  popover.style.position = '';
  popover.style.top = '';
  popover.style.left = '';
  popover.style.right = '';
  popover.style.width = '';
  popover.style.transform = '';
};

const FeaturedCarsPriceFilter = ({
  filter,
  isOpen,
  isActive,
  minPrice,
  maxPrice,
  buttonLabel,
  onToggle,
  onRangeChange,
  onApply,
  setBtnWrapperRef,
  setPopoverWrapperRef,
}) => {
  const isLuxury = filter.key === 'luxury';
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);

  // keep parent refs for click-outside handling
  const mergeButtonRef = useCallback((node) => {
    buttonRef.current = node;
    setBtnWrapperRef(node);
  }, [setBtnWrapperRef]);

  const mergePopoverRef = useCallback((node) => {
    popoverRef.current = node;
    setPopoverWrapperRef(node);
  }, [setPopoverWrapperRef]);

  // pin popover inside the viewport on small screens
  const updatePopoverPosition = useCallback(() => {
    const button = buttonRef.current;
    const popover = popoverRef.current;

    if (!button || !popover) {
      return;
    }

    if (!window.matchMedia(MOBILE_POPOVER_QUERY).matches) {
      resetPopoverPosition(popover);
      return;
    }

    const { bottom } = button.getBoundingClientRect();

    // fixed width with side insets works for left and right filter buttons
    popover.style.position = 'fixed';
    popover.style.top = `${bottom + 8}px`;
    popover.style.left = `${POPOVER_VIEWPORT_INSET}px`;
    popover.style.right = `${POPOVER_VIEWPORT_INSET}px`;
    popover.style.width = 'auto';
    popover.style.transform = 'none';
  }, []);

  // useEffect (not useLayoutEffect) — popover only opens on client click; avoids SSR warning
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    updatePopoverPosition();
    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);

    return () => {
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition, true);

      if (popoverRef.current) {
        resetPopoverPosition(popoverRef.current);
      }
    };
  }, [isOpen, minPrice, maxPrice, updatePopoverPosition]);

  const renderPriceRangeLabel = () => {
    if (isLuxury) {
      return (
        <p className={cx(cs.commonContentText, cs.fontWeightMedium, cs.fontDMSans)}>
          <CurrencyConverter amount={minPrice} from="USD" />
          <span>{'+ '}</span>
          <FormattedMessage {...messages.perNight} />
        </p>
      );
    }

    return (
      <p className={cx(cs.commonContentText, cs.fontWeightMedium, cs.fontDMSans)}>
        <CurrencyConverter amount={minPrice} from="USD" />
        <span>{' - '}</span>
        <CurrencyConverter amount={maxPrice} from="USD" />
        <span>{'  '}</span>
        <FormattedMessage {...messages.perNight} />
      </p>
    );
  };

  return (
    <div
      className={s.filterButtonContainer}
      ref={mergeButtonRef}
    >
      <button
        type="button"
        className={cx(s.filterBtn, isActive && s.filterBtnActive)}
        onClick={onToggle}
      >
        <FeaturedCarFilterIcon type={filter.icon} />
        {buttonLabel}
      </button>
      {isOpen && (
        <div
          className={s.searchFilterPopover}
          ref={mergePopoverRef}
        >
          <div className={s.searchFilterPopoverContent}>
            {renderPriceRangeLabel()}
            <div className={s.priceRangeContainer}>
              {isLuxury ? (
                <Slider
                  min={LUXURY_PRICE_MIN_USD}
                  max={FEATURED_PRICE_SLIDER_MAX}
                  value={minPrice}
                  onChange={(value) => onRangeChange([value, LUXURY_PRICE_MAX_USD])}
                  step={1}
                />
              ) : (
                <Slider
                  range
                  min={LOWEST_PRICE_MIN_USD}
                  max={LOWEST_PRICE_MAX_USD}
                  value={[minPrice, maxPrice]}
                  onChange={onRangeChange}
                  allowCross={false}
                  step={1}
                />
              )}
            </div>
            <div className={cx(
              s.searchFilterPopoverFooter,
              s.displayTable,
              s.popoverFooterDivider,
            )}>
              <div className={cx('text-left', s.displayTableCell)} />
              <div className={cx(s.floatRight, 'floatLeftRTL')}>
                <button
                  type="button"
                  className={s.priceFilterApplyBtn}
                  onClick={onApply}
                >
                  <FormattedMessage {...messages.apply} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

FeaturedCarsPriceFilter.propTypes = {
  filter: PropTypes.shape({
    key: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  isActive: PropTypes.bool.isRequired,
  minPrice: PropTypes.number.isRequired,
  maxPrice: PropTypes.number.isRequired,
  buttonLabel: PropTypes.node.isRequired,
  onToggle: PropTypes.func.isRequired,
  onRangeChange: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  setBtnWrapperRef: PropTypes.func.isRequired,
  setPopoverWrapperRef: PropTypes.func.isRequired,
};

export default withStyles(s, cs, sliderStyles)(FeaturedCarsPriceFilter);
